
import React, { useEffect, useRef, memo } from "react";
import { Chart } from "chart.js/auto";

interface ChartsSectionProps {
  financeData: any;
}


const safeColor = (input: any): string => {
  if (!input) return "#000";

  if (typeof input === "string") return input;

  if (Array.isArray(input)) {
    const first = input.find((c) => typeof c === "string");
    return first || "#000";
  }

  return "#000";
};


const ChartsSection: React.FC<ChartsSectionProps> = ({ financeData }) => {
  const donutRef = useRef<HTMLCanvasElement | null>(null);
  const lineRef = useRef<HTMLCanvasElement | null>(null);
  const donutChartRef = useRef<Chart | null>(null);
  const lineChartRef = useRef<Chart | null>(null);

  const COLORS = {
    sales: "#2563eb",
    installs: "#16a34a",
    removals: "#f59e0b",
  };

  const formatCurrency = (val: any) =>
    `$${Number(val || 0).toLocaleString(undefined, {
      maximumFractionDigits: 2,
    })}`;

  useEffect(() => {
    if (!financeData) return;

    // Destroy old charts
    if (donutChartRef.current) donutChartRef.current.destroy();
    if (lineChartRef.current) lineChartRef.current.destroy();

    /* -----------------------  DONUT CHART  ----------------------- */
    if (donutRef.current) {
      const ctx = donutRef.current.getContext("2d")!;
      donutChartRef.current = new Chart(ctx, {
        type: "doughnut",
        data: {
          labels: ["Sales Orders", "Installations", "Removals"],
          datasets: [
            {
              data: [
                financeData.sales_revenue?.current || 0,
                financeData.installation_revenue?.current || 0,
                financeData.removal_revenue?.current || 0,
              ],
              backgroundColor: [COLORS.sales, COLORS.installs, COLORS.removals],
              borderColor: "#fff",
              borderWidth: 4,
              spacing: 4,                // <-- ADD GAP BETWEEN COLORS
              // cutout: "60%",             // <-- BIGGER DONUT RING
            },
          ],
        },
        options: {
          cutout: "60%",
          responsive: true,
          maintainAspectRatio: false,

          plugins: {
            legend: { display: false },

            tooltip: {
              backgroundColor: "#ffffff",
              borderColor: "#d1d5db",
              borderWidth: 1,
              displayColors: false,
              padding: 10,

              titleColor: "#000000", //  🔥 FIXED → Title black
              bodyColor: "#000000",  //  🔥 FIXED → Text black

              callbacks: {
                label: (ctx) => `${ctx.label} : ${formatCurrency(ctx.parsed)}`,
              },
            },
          },
        }

      });
    }

    /* -----------------------  LINE CHART  ----------------------- */
    const trends = financeData?.monthly_trends || [];
    if (!trends.length) return;

    const labels = trends.map((t: any) => t.period).reverse();
    const salesData = trends.map((t: any) => t.sales || 0).reverse();
    const installsData = trends.map((t: any) => t.installations || 0).reverse();
    const removalData = trends.map((t: any) => t.removals || 0).reverse();


    const ctx = lineRef.current!.getContext("2d")!;

    if (!ctx) return;

    lineChartRef.current = new Chart(ctx, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Sales Orders",
            data: salesData,
            borderColor: COLORS.sales,
            backgroundColor: COLORS.sales,
            tension: 0.3,
            fill: false,
            spanGaps: true,
            pointRadius: 3,
            pointHoverRadius: 4,
            borderWidth: 2,
          },
          {
            label: "Installations",
            data: installsData,
            borderColor: COLORS.installs,
            backgroundColor: COLORS.installs,
            tension: 0.3,
            fill: false,
            spanGaps: true,
            pointRadius: 3,
            pointHoverRadius: 4,
            borderWidth: 2,
          },
          {
            label: "Removals",
            data: removalData,
            borderColor: COLORS.removals,
            backgroundColor: COLORS.removals,
            tension: 0.3,
            fill: false,
            spanGaps: true,
            pointRadius: 3,
            pointHoverRadius: 4,
            borderWidth: 2,
          },
        ],
      },

      options: {
        responsive: true,
        maintainAspectRatio: false,

        interaction: {
          mode: "index",
          intersect: false,
        },

        plugins: {
          legend: { display: false },

          tooltip: {
            enabled: true,
            backgroundColor: "#ffffff",
            borderColor: "#d1d5db",
            borderWidth: 1,
            titleColor: "#000000",
            titleFont: { size: 14, weight: "bold" },
            bodyColor: "#000000",
            bodyFont: { size: 13 },
            padding: 12,
            cornerRadius: 8,
            displayColors: true,
            boxPadding: 6,
            usePointStyle: true,
            boxHeight: 10,
            boxWidth: 10,

            callbacks: {
              title: (items) => {
                const month = items[0].label || "";
                return month; // full month name
              },

              label: (ctx) => {
                const value = Number(ctx.raw ?? 0).toFixed(2);
                return `${ctx.dataset.label} : ${Number(value).toLocaleString()}`;
              },

              labelColor: (ctx) => {
                let color = "#000";

                const bc = ctx.dataset.borderColor;
                if (typeof bc === "string") color = bc;
                else if (Array.isArray(bc)) color = bc[0];

                return {
                  borderColor: color,
                  backgroundColor: color,
                };
              },
            },
          },




        },

        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: (v) => Number(v).toLocaleString(), // ❌ remove $ symbol
              color: "#374151", // darker tick text
              font: { size: 12 },
            },
            grid: {
              color: "rgba(0,0,0,0.15)", // darker grid lines
            },
          },

          x: {
            ticks: {
              color: "#374151",
              font: { size: 12 },

              callback: function (value: string | number, index: number, ticks: any) {
                const chart = this.chart;

                // Full month from backend (example: "October")
                const fullLabel =
                  chart?.data?.labels?.[index] &&
                    typeof chart.data.labels[index] === "string"
                    ? (chart.data.labels[index] as string)
                    : "";

                // convert full month to short (Oct → Nov → Dec)
                const shortLabel = fullLabel.substring(0, 3);

                return shortLabel;
              },
            },

            grid: {
              color: "rgba(0,0,0,0.15)",
            },
          },


        },
      }

    });

    return () => {
      if (donutChartRef.current) donutChartRef.current.destroy();
      if (lineChartRef.current) lineChartRef.current.destroy();
    };
  }, [financeData]);


  return (
    <div className="row g-3 mb-4">

      {/* Donut chart */}
      <div className="col-12 col-lg-4">
        <div
          className="card shadow-sm h-100"
          style={{ borderRadius: "12px", border: "1px solid #E5E7EB" }}
        >
          <div className="card-body">
            <h4 className="fw-bold mb-3">Revenue Breakdown</h4>

            {/* Small donut height */}
            <div style={{ height: "210px" }}>
              <canvas ref={donutRef}></canvas>
            </div>

            {/* Legend below donut */}
            <ul className="list-unstyled mt-3 mb-0">
              <li className="mb-2">
                <span
                  className="rounded-circle me-2"
                  style={{
                    backgroundColor: COLORS.sales,
                    width: "10px",
                    height: "10px",
                    display: "inline-block",
                  }}
                ></span>
                <strong>Sales Orders:</strong>{" "}
                {formatCurrency(financeData?.sales_revenue?.current)}
              </li>

              <li className="mb-2">
                <span
                  className="rounded-circle me-2"
                  style={{
                    backgroundColor: COLORS.installs,
                    width: "10px",
                    height: "10px",
                    display: "inline-block",
                  }}
                ></span>
                <strong>Installations:</strong>{" "}
                {formatCurrency(financeData?.installation_revenue?.current)}
              </li>

              <li>
                <span
                  className="rounded-circle me-2"
                  style={{
                    backgroundColor: COLORS.removals,
                    width: "10px",
                    height: "10px",
                    display: "inline-block",
                  }}
                ></span>
                <strong>Removals:</strong>{" "}
                {formatCurrency(financeData?.removal_revenue?.current)}
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Line chart */}
      <div className="col-12 col-lg-8">
        <div
          className="card shadow-sm h-100"
          style={{ borderRadius: "12px", border: "1px solid #E5E7EB" }}
        >
          <div className="card-body">
            <h4 className="fw-bold mb-3">Monthly Order Trends</h4>

            <div style={{ height: "320px" }}>
              <canvas ref={lineRef}></canvas>
            </div>

            <div className="d-flex align-items-center gap-4 mt-3 flex-wrap">
              <span className="d-flex align-items-center" style={{ fontSize: "0.85rem" }}>
                <span
                  className="rounded-circle me-2"
                  style={{
                    backgroundColor: COLORS.sales,
                    width: "10px",
                    height: "10px",
                  }}
                ></span>
                <span className="text-gray-600">
                  <strong>Sales Orders:</strong>{" "}
                  {formatCurrency(financeData?.sales_revenue?.current)}
                </span>
              </span>

              <span className="d-flex align-items-center" style={{ fontSize: "0.85rem" }}>
                <span
                  className="rounded-circle me-2"
                  style={{
                    backgroundColor: COLORS.installs,
                    width: "10px",
                    height: "10px",
                  }}
                ></span>
                <span className="text-gray-600">
                  <strong>Installations:</strong>{" "}
                  {formatCurrency(financeData?.installation_revenue?.current)}
                </span>
              </span>

              <span className="d-flex align-items-center" style={{ fontSize: "0.85rem" }}>
                <span
                  className="rounded-circle me-2"
                  style={{
                    backgroundColor: COLORS.removals,
                    width: "10px",
                    height: "10px",
                  }}
                ></span>
                <span className="text-gray-600">
                  <strong>Removals:</strong>{" "}
                  {formatCurrency(financeData?.removal_revenue?.current)}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .card:hover {
          transform: translateY(-1px);  
        }
          .text-gray-600 {
          color: #4a5565;
        }
      `}</style>
    </div>
  );
};

export default ChartsSection;






